import { useState } from 'react'
import { IconButtonWithTooltip, useGetIdentity, useGetList, useTheme } from 'react-admin'
import { FormControl, Select as MUIselect, InputLabel, MenuItem, Stack, Box, Typography, useMediaQuery } from '@mui/material'

import useEventsInfo from '../../hooks/useEvents'

import TasksList from './TasksList'
import EventsList from './EventsList'
import WeeklyLeads from './WeeklyLeads'
import LeadsInscrits from './LeadsInscrits'
import { getRole } from '../../common/roles'
import { getInputSx } from '../../Design/CustomInput'
import ProjectTimeline from './ProjectTimeline'
import ConversionFunnel from './ConversionFunnel'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

const DashboardBase = () => {
  const [theme] = useTheme()
  const isDark = theme === 'dark'
  const isMobile = useMediaQuery(
    t => t.breakpoints.down('md'),
    { noSsr: true }
  )
  const { data: identity } = useGetIdentity()
  const { eventsList: fetchEvents } = useEventsInfo(identity?.center?.id, identity)
  const isAdmin = identity?.role?.grade === getRole('admin')

  const today = new Date()

  const lastMonday = new Date()
  lastMonday.setHours(0, 0, 0, 0)
  lastMonday.setDate(lastMonday.getDate() - (lastMonday.getDay() + 6) % 7)

  const firstOfTheMonth = new Date()
  firstOfTheMonth.setHours(0, 0, 0, 0)
  firstOfTheMonth.setDate(1)

  const academicYearStart = new Date()
  if (today.getMonth() < 7) { // If before August (Jan - Jul), use last year's August
    academicYearStart.setFullYear(today.getFullYear() - 1, 7, 1) // August 1st of last year
  } else {
    academicYearStart.setFullYear(today.getFullYear(), 7, 1) // August 1st of this year
  }
  academicYearStart.setHours(0, 0, 0, 0)

  const [filteredDate, setFilteredDate] = useState(lastMonday.toISOString())
  const [filteredCenter, setFilteredCenter] = useState('')
  const [copied, setCopied] = useState(false)

  // Fetch centers list for admin filter
  const { data: centersData } = useGetList(
    'centers',
    {
      pagination: { page: 1, perPage: 999 },
      sort: { field: 'name', order: 'ASC' },
    },
    { enabled: isAdmin }
  )

  // Using useGetList hook for leads data
  const { data: leadsData, isLoading: leadsLoading } = useGetList(
    'leads',
    {
      pagination: { page: 1, perPage: isAdmin ? 999999 : 99999 },
      sort: { field: 'created_at', order: 'DESC' },
      meta: { columns: ['id', 'created_at', 'current_year_id', 'status_inscription_ids', 'lead_source_id', 'center_id', 'lead_events(event_id, event:event_id(type_id, name, date))'] }
    }
  )

  // Using useGetList hook for tasks data
  const { data: tasksData, isLoading: tasksLoading } = useGetList(
    'lead_tasks',
    {
      pagination: { page: 1, perPage: 9999 },
      sort: { field: 'id', order: 'ASC' },
      meta: { columns: ['id', 'progress', 'created_at', 'lead:lead_id(center_id, current_year_id)', 'task:task_id(name)'] }
    }
  )

  // Filter leads by current year (and center if selected)
  const currentYearLeads = leadsData
    ? leadsData.filter(lead =>
        lead.current_year_id === process.env.REACT_APP_CURRENT_YEAR_ID &&
        (!filteredCenter || lead.center_id === filteredCenter)
      )
    : null

  // Process the data once fetched
  const dashboardData = {
    numberOfNewLeads: currentYearLeads
      ? currentYearLeads.filter(lead => new Date(lead.created_at) >= new Date(filteredDate)).length
      : 0,
    numberOfLeadsInscrits: currentYearLeads
      ? currentYearLeads.filter(lead =>
          new Date(lead.created_at) >= new Date(filteredDate) &&
          lead.status_inscription_ids !== null
        ).length
      : 0,
    numberOfLeadsInscritsMep: currentYearLeads
      ? currentYearLeads.filter(lead =>
          new Date(lead.created_at) >= new Date(filteredDate) &&
          lead.status_inscription_ids !== null &&
          lead.lead_source_id === process.env.REACT_APP_LEAD_SOURCE_MEP
        ).length
      : 0,
    tasksInfo: tasksData
      ? tasksData.filter(task =>
          task.lead !== null &&
          task.task !== null &&
          task.lead.current_year_id === process.env.REACT_APP_CURRENT_YEAR_ID &&
          (!filteredCenter || task.lead.center_id === filteredCenter) &&
          new Date(task.created_at) >= new Date(filteredDate)
        )
      : null,
    eventsInfo: fetchEvents
      ? fetchEvents.filter(event =>
          (event.type_id === process.env.REACT_APP_EVENT_TYPE_MISSIONLYCEE ||
           event.type_id === process.env.REACT_APP_EVENT_TYPE_INTERVENTION) &&
          event.current_year_id === process.env.REACT_APP_CURRENT_YEAR_ID &&
          (!filteredCenter || event.center_id === filteredCenter)
        )
      : null
  }

  const handleCopy = () =>
    navigator.clipboard.writeText(identity?.center?.id)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 3000)
      })
      .catch(err => {
        console.error('Failed to copy: ', err)
      })

  return (
    <>
      {/* toolbar */}
      <Stack spacing={2} direction={isMobile ? 'column' : 'row'} justifyContent="space-between">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: 1,
            },
            p: 1
          }}
          onClick={handleCopy}
        >
          <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ flexGrow: 1 }}>
            <b>ID du centre {identity?.center?.name} :</b> {identity?.center?.id}
          </Typography>

          <IconButtonWithTooltip label={copied ? "Copié !" : "Copier l'ID"} size="small" edge="end">
            {copied ? <CheckCircleIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButtonWithTooltip>
        </Box>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={1}>
          {isAdmin && centersData && (
            <Box sx={{ width: isMobile ? '100%' : 200 }}>
              <FormControl variant="outlined" sx={{ m: 0.5, minWidth: '100%', ...getInputSx(isDark) }} size={isMobile ? 'small' : 'medium'}>
                <InputLabel id="centerLabel">Centre</InputLabel>
                <MUIselect
                  labelId="centerLabel"
                  value={filteredCenter}
                  onChange={e => setFilteredCenter(e.target.value)}
                  label="Centre"
                >
                  <MenuItem value="">Tous les centres</MenuItem>
                  {centersData.map(center => (
                    <MenuItem key={center.id} value={center.id}>{center.name}</MenuItem>
                  ))}
                </MUIselect>
              </FormControl>
            </Box>
          )}

          <Box sx={{ width: isMobile ? '100%' : 200 }}>
            <FormControl variant="outlined" sx={{ m: 0.5, minWidth: '100%', ...getInputSx(isDark) }} size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="monthLabel">Voir depuis</InputLabel>
              <MUIselect
                labelId="monthLabel"
                value={filteredDate}
                onChange={e => setFilteredDate(e.target.value)}
                label="Voir depuis"
              >
                <MenuItem value={lastMonday.toISOString()}>Cette semaine</MenuItem>
                <MenuItem value={firstOfTheMonth.toISOString()}>Mois en cours</MenuItem>
                <MenuItem value={academicYearStart.toISOString()}>Année en cours</MenuItem>
              </MUIselect>
            </FormControl>
          </Box>
        </Stack>
      </Stack>

      {isAdmin && <ProjectTimeline identity={identity} />}

      {/* conversion funnel */}
      {isAdmin && (
        <ConversionFunnel
          leadsData={currentYearLeads}
          filteredDate={filteredDate}
          loading={leadsLoading}
        />
      )}

      {/* stats */}
      <Stack spacing={2} mt={2} direction={isMobile ? 'column' : 'row'}>
        <Stack spacing={2} mt={isMobile ? 0 : 2} direction="column" sx={{ flex: 1 }}>
          <WeeklyLeads
            value={dashboardData.numberOfNewLeads}
            loading={leadsLoading}
          />

          <LeadsInscrits
            value={dashboardData.numberOfLeadsInscrits}
            mepCount={dashboardData.numberOfLeadsInscritsMep}
            loading={leadsLoading}
          />
        </Stack>

        <TasksList
          tasksInfo={dashboardData.tasksInfo}
          loading={tasksLoading}
        />

        <EventsList
          eventsInfo={dashboardData.eventsInfo}
          date={filteredDate}
        />
      </Stack>
    </>
  )
}

export default DashboardBase